/**
 * SubComponentWrapper - Makes any element selectable within a complex component
 *
 * This wrapper intercepts clicks in editing mode to select the sub-component,
 * shows selection/hover states, and prevents the click from bubbling to the parent.
 *
 * Usage:
 * ```tsx
 * <SubComponentWrapper subComponentId={getSubComponentId('view-details')}>
 *   <button onClick={handleClick}>View Details</button>
 * </SubComponentWrapper>
 * ```
 */

import React, { useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useInteractionContext } from '@/components/shared/rendering/InteractionContextRuntime';

interface SubComponentWrapperProps {
  /** The sub-component ID (e.g., 'tank-1::view-details') */
  subComponentId: string;
  /** The element to wrap */
  children: React.ReactNode;
  /** Additional styles for the wrapper */
  style?: React.CSSProperties;
  /** Optional className */
  className?: string;
  /** Whether to render as inline element (span) instead of block (div) */
  inline?: boolean;
  /** Disable selection (pass through clicks) */
  disabled?: boolean;
}

export const SubComponentWrapper: React.FC<SubComponentWrapperProps> = ({
  subComponentId,
  children,
  style,
  className,
  inline = false,
  disabled = false,
}) => {
  // Get mode from InteractionContext (same source as ComponentWrapper)
  const { mode } = useInteractionContext();
  const selectComponent = useEditorStore((state) => state.selectComponent);
  const isComponentSelected = useEditorStore((state) => state.isComponentSelected);
  const getComponent = useEditorStore((state) => state.getComponent);
  const hoveredComponent = useEditorStore((state) => state.hoveredComponent);
  const setHoveredComponent = useEditorStore((state) => state.setHoveredComponent);

  const isSelected = isComponentSelected(subComponentId);
  const isHovered = hoveredComponent === subComponentId;
  // Allow selection in both editing and viewing modes (AI-first workflow)
  const canSelect = mode === 'editing' || mode === 'viewing';

  // Check if sub-component is actually registered in the store
  const isRegistered = !!getComponent(subComponentId);

  // ALWAYS render the wrapper for structural layout, but only add interactivity when appropriate
  const canInteract = canSelect && isRegistered && !disabled;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Only allow selection if sub-component is registered in the store
      if (!canInteract) return;

      // Stop propagation to prevent parent component selection
      e.stopPropagation();
      e.preventDefault();

      // Select this sub-component
      selectComponent(subComponentId);
    },
    [canInteract, selectComponent, subComponentId]
  );

  const handleMouseEnter = useCallback(() => {
    // Only show hover if sub-component is registered
    if (canInteract) {
      setHoveredComponent(subComponentId);
    }
  }, [canInteract, setHoveredComponent, subComponentId]);

  const handleMouseLeave = useCallback(() => {
    if (canInteract) {
      setHoveredComponent(undefined);
    }
  }, [canInteract, setHoveredComponent]);

  // Build wrapper styles - only add selection/hover styles when interactive
  const wrapperStyle: React.CSSProperties = {
    ...style,
    position: 'relative',
    // Only add interactive cursor and selection indicators when sub-component can be selected
    ...(canInteract && {
      cursor: 'pointer',
      // Selection indicator - Primary color from design system
      outline: isSelected ? '2px solid hsl(var(--primary))' : isHovered ? '1px dashed hsl(var(--primary))' : 'none',
      outlineOffset: '2px',
      // Subtle background on hover
      backgroundColor: isHovered && !isSelected ? 'hsl(var(--primary) / 0.05)' : undefined,
      // Smooth transitions
      transition: 'outline 0.15s ease, background-color 0.15s ease',
      // Ensure clicks work
      pointerEvents: 'auto',
    }),
  };

  const Tag = inline ? 'span' : 'div';

  return (
    <Tag
      onClick={canInteract ? handleClick : undefined}
      onMouseEnter={canInteract ? handleMouseEnter : undefined}
      onMouseLeave={canInteract ? handleMouseLeave : undefined}
      className={className}
      style={wrapperStyle}
      data-subcomponent-id={subComponentId}
      data-selected={canInteract ? isSelected : undefined}
      data-hovered={canInteract ? isHovered : undefined}
    >
      {children}
    </Tag>
  );
};

export default SubComponentWrapper;
